/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import {
  useAuth,
  useGetSavedList,
  useSearchPageParams,
  useI18N,
  usePostDeleteSavedList,
  useBusinessInterests,
  useHomeVenues,
  useVenueDetailsBySlug,
} from "common/hooks";
import { Storage } from "common/storage";
import { useDarkMode } from "@/hooks/useDarkMode";
import { useAtom } from "jotai";
import {
  leftPanelExpandedAtom,
  savedPlaceByUserIdToViewAtom,
  savedPlaceIdToViewDataChangedAtom,
  savedPlaceSortByToViewAtom,
  venueDetailsOpenedAtom,
} from "@/state/atoms";
import { savedPlaceIdToViewAtom } from "@/state/atoms";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import AnalyticsService from "@/services/analytics";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import SavePlaceMenu from "@/components/SavedPlaces/SavePlaceMenu";
import { RWebShare } from "react-web-share";
import { Helmet } from "react-helmet";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import { useCustomToast } from "@/hooks/useCustomToast";
import { cn } from "@/lib/utils";
import { ViewPager, Frame, Track, View } from "@/lib/react-view-pager-compat";
import VenueList from "@/features/home/VenueList";
import { CaretDownIcon, ListBulletsIcon, MapTrifoldIcon } from "@phosphor-icons/react";
import { Venue, BusinessInterestType } from "common/types";
import { StandardButton } from "@/components/common/StandardButton";
import { ArrowUp } from "lucide-react";
import VenueDetailBasics from "@/components/VenueDetailBasic/BasicVenueDetails";
import BasicMapView from "@/components/VenueDetailBasic/BasicMapView";
import AccessibilityDetailView from "@/components/VenueDetailBasic/AccessibilityDetailView";
import PhotosDetailView from "@/components/VenueDetailBasic/PhotosDetailView";
import InterestNotificationModal from "@/components/Notifications/NotificationModal/InterestNotificationModal";
import { useAuthModals, AuthModalScreen } from "@/hooks/useAuthModals";
import { useGeoLocation } from "@/hooks/useGeoLocation";

export const Route = createFileRoute(
  "/savedplacedetails/$savedPlaceId/user/$userId",
)({
  component: SavedPlaceDetails,
});

function SavedPlaceDetails() {
  const { query, venue } = Route.useSearch() as any;

  const [, setSavedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [, setSavedPlaceByUserIdToView] = useAtom(savedPlaceByUserIdToViewAtom);
  const [, setSavedPlaceSortByToView] = useAtom(savedPlaceSortByToViewAtom);
  const [savedPlaceIdToViewDataChanged, setSavedPlaceIdToViewDataChanged] =
    useAtom(savedPlaceIdToViewDataChangedAtom);
  const [mapRef] = useState<google.maps.Map | null>(null);

  const [currentSort, setCurrentSort] = useState<
    "latest" | "earliest" | "alphabetical"
  >("latest");

  const { selectedVenue, setSelectedVenue } = useSelectedVenue();

  const { userId: userId, savedPlaceId: savedPlaceId } = Route.useParams();
  const isDark = useDarkMode();
  const { user, isLoggedIn } = useAuth();
  const i18n = useI18N();
  const navigate = useNavigate({ from: "/" });

  const showToast = useCustomToast();

  const isUserOwner = `${user?.id}` === userId ? true : false;

  const router = useRouter();
  const onBack = () => router.history.back();

  const goBackToSavedPlaces = () => {
    navigate({ to: "/mysavedplaces" });
    AnalyticsService.trackGAEventHandler("viewListClickedBackButton");
    AnalyticsService.trackMx("viewListClickedBackButton", {});
  };

  const [, setIsVenueDetailsOpen] = useAtom(venueDetailsOpenedAtom);
  const [isLeftPaneVisible, setIsLeftPaneVisible] = useAtom(
    leftPanelExpandedAtom,
  );
  const checkScreenWidth = () => window.innerWidth <= 768;

  const trackMobile = useRef<Track>(null);
  const [currentVenueTypePage, setCurrentVenueTypePage] = useState(0);
  const [mobileListView, setMobileListView] = useState(true);
  const isSyncingFromUrl = useRef(false);

  const mobileVenueDetailsScrollRef = useRef<HTMLDivElement>(null);
  const [mobileScrollToTopVisible, setMobileScrollToTopVisible] =
    useState(false);

  // Accessibility: Refs for focus management when switching between views
  const venueListContainerRef = useRef<HTMLDivElement>(null);
  const venueDetailsContainerRef = useRef<HTMLDivElement>(null);
  const venueDetailsScrollRef = useRef<HTMLDivElement>(null);
  const [scrollToTopVisible, setScrollToTopVisible] = useState(false);

  // State for AccessibilityDetailView overlay
  const [showAccessibilityDetail, setShowAccessibilityDetail] = useState(false);
  const [accessibilityDetailSpaceId, setAccessibilityDetailSpaceId] = useState<string>("");

  // State for PhotosDetailView overlay
  const [showPhotosDetail, setShowPhotosDetail] = useState(false);
  const [photosDetailCategoryId, setPhotosDetailCategoryId] = useState<number | null>(null);

  // load saved places details from the server
  const {
    error: getSavedListError,
    savedList,
    refetch: getSavedListRefetch,
  } = useGetSavedList(savedPlaceId, userId);

  // Delete save place list
  const {
    deleteList,
    error: deleteListError,
    result: deleteListResult,
  } = usePostDeleteSavedList();

  const [sortByText, setSortByText] = useState("Date added (Newest)");

  // Notification modal state
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [selectedVenueForNotification, setSelectedVenueForNotification] =
    useState<{
      id: string;
      name: string;
      interestId?: string;
      interestedType?: string;
      notifyAccessibilityInformation: boolean;
      notifyLiveAlert: boolean;
    } | null>(null);

  const { showModal, isAuthModalVisible } = useAuthModals();

  const { location } = useGeoLocation();

  // Fetch venue details when showing accessibility or photos detail view
  const { data: venueDetailsData } = useVenueDetailsBySlug({
    slug: selectedVenue?.business?.slug || "",
    latitude: location?.latitude?.toString(),
    longitude: location?.longitude?.toString(),
    accessToken: Storage.getCurrentUserToken() || "",
    enabled:
      (showAccessibilityDetail || showPhotosDetail) &&
      !!selectedVenue?.business?.slug &&
      !!location,
  });

  // Get business interests and venues for notifications
  const { data: businessInterests } = useBusinessInterests(false);
  const { locations = [], isLoading: isLocationsLoading } = useHomeVenues();

  // Create a Set of venue IDs with notifications enabled
  const notificationVenueIds = useMemo(() => {
    if (!businessInterests) return new Set<number>();

    return new Set(
      businessInterests
        .filter(
          (interest) =>
            interest.notify_accessibility_information === 1 ||
            interest.notify_live_alert === 1,
        )
        .map((interest) => interest.business_id),
    );
  }, [businessInterests]);

  // Create a Map for quick lookup of business interest details
  const businessInterestMap = useMemo(() => {
    if (!businessInterests) return new Map<number, BusinessInterestType>();

    return new Map<number, BusinessInterestType>(
      businessInterests.map(
        (interest) =>
          [interest.business_id, interest] as [number, BusinessInterestType],
      ),
    );
  }, [businessInterests]);

  useEffect(() => {
    switch (currentSort) {
      case "latest":
        setSortByText("Date added (Newest)");
        break;
      case "earliest":
        setSortByText("Date added (Oldest)");
        break;
      case "alphabetical":
        setSortByText("Alphabetical");
        break;
      default:
        setSortByText("Date added (Newest)");
    }
  }, [currentSort]);

  useEffect(() => {
    if (deleteListResult) {
      AnalyticsService.trackGAEventHandler("deleteListClickedListDeleted");
      AnalyticsService.trackMx("deleteListClickedListDeleted", {
        SavedPlaceName: savedList?.saved_places[0].name,
        SavedPlaceId: savedList?.saved_places[0].id,
        SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
      });

      //redirect to my saved places page
      navigate({ to: "/mysavedplaces" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteListResult]);

  useEffect(() => {
    if (deleteListError) {
      AnalyticsService.trackGAEventHandler("viewListErrorDeletingList");
      AnalyticsService.trackMx("viewListErrorDeletingList", {
        SavedPlaceName: savedList?.saved_places[0].name,
        SavedPlaceId: savedList?.saved_places[0].id,
        SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
      });
      showToast(deleteListError.message, "error");
    }
  }, [deleteListError]);

  const { setSearchPageParams } = useSearchPageParams();

  useEffect(() => {
    if (savedPlaceIdToViewDataChanged) {
      console.log(
        "savedPlaceIdToViewDataChanged",
        savedPlaceIdToViewDataChanged,
      );
      setSavedPlaceIdToViewDataChanged(false);
      getSavedListRefetch();

      setSearchPageParams((prev) => ({
        ...prev,       
        saved_place_id: savedPlaceId,
        saved_place_user_id: userId,
        zoom: mapRef?.getZoom().toString() || "14",
        searchThisAreaClicked: "no",
      }));
    }
  }, [
    getSavedListRefetch,
    mapRef,
    savedPlaceId,
    savedPlaceIdToViewDataChanged,
    setSearchPageParams,
    setSavedPlaceIdToViewDataChanged,
    userId,
  ]);

  useEffect(() => {
    // Function to handle screen resize
    const handleResize = () => {
      const isSmallScreen = checkScreenWidth();
      if (isSmallScreen) {
        setIsLeftPaneVisible(true);
      }
    };

    // Set initial visibility based on current screen width
    handleResize();

    // Add event listener to window
    window.addEventListener("resize", handleResize);

    // Clean up event listener
    return () => window.removeEventListener("resize", handleResize);
  }, [setIsLeftPaneVisible]);

  useEffect(() => {
    console.log("Dark mode is", isDark ? "enabled" : "disabled");
  }, [isDark]);

  useEffect(() => {
    setIsVenueDetailsOpen(false);
    setIsLeftPaneVisible(true);
  }, [setIsLeftPaneVisible, setIsVenueDetailsOpen]);

  useEffect(() => {
    if (savedPlaceId) {
      console.log("INSIDEW savedPlaceId", savedPlaceId);
      setSavedPlaceIdToView(savedPlaceId);
      setSavedPlaceByUserIdToView(userId);
      setSavedPlaceSortByToView(currentSort);

      setSearchPageParams((prev) => ({
        ...prev,
        search: "",
        tagging_mode: "off",
        saved_place_id: savedPlaceId,
        saved_place_user_id: userId,
        zoom: mapRef?.getZoom().toString() || "14",
        searchThisAreaClicked: "no",
        unixTimestamp: Math.floor(Date.now() / 1000),
        search_type: "",
        search_value: "",
        // Don't set to null - preserve existing coordinates or leave undefined to trigger initialization
        ...(prev.latitude && { latitude: prev.latitude }),
        ...(prev.longitude && { longitude: prev.longitude }),
        address: "",
        types: "",
        accessibilities: "",
        showVenueOptions: "All",
        addinfo: "",
        placesToReplaceString: "",
        section_filter: undefined,
        mapper_category: undefined,
      }));
    }
  }, [
    currentSort,
    mapRef,
    savedPlaceId,
    setSearchPageParams,
    setSavedPlaceByUserIdToView,
    setSavedPlaceIdToView,
    setSavedPlaceSortByToView,
    userId,
  ]);

  useEffect(() => {
    if (getSavedListError) {
      AnalyticsService.trackGAEventHandler("viewListErrorLoading");
      AnalyticsService.trackMx("viewListErrorLoading", {});

      showToast(getSavedListError.message, "error");

      setTimeout(() => {
        //redirect to my saved places page
        navigate({ to: "/mysavedplaces" });
      }, 500);
    }
  }, [getSavedListError, navigate]);

  const handleDeleteList = async () => {
    console.log("handleDeleteList");

    //Delete list
    deleteList({
      savedPlaceId: savedPlaceId,
    });
  };

  const handleListSort = (value) => {
    console.log("handleListSort value", value);

    if (value === "latest") {
      AnalyticsService.trackGAEventHandler(
        "viewListClickedSortSavedPlacesLatest",
      );
      AnalyticsService.trackMx("viewListClickedSortSavedPlacesLatest", {
        SavedPlaceName: savedList?.saved_places[0].name,
        SavedPlaceId: savedList?.saved_places[0].id,
        SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
      });
    } else if (value === "earliest") {
      AnalyticsService.trackGAEventHandler(
        "viewListClickedSortSavedPlacesEarliest",
      );
      AnalyticsService.trackMx("viewListClickedSortSavedPlacesEarliest", {
        SavedPlaceName: savedList?.saved_places[0].name,
        SavedPlaceId: savedList?.saved_places[0].id,
        SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
      });
    } else {
      AnalyticsService.trackGAEventHandler(
        "viewListClickedSortSavedPlacesAlphabetical",
      );
      AnalyticsService.trackMx("viewListClickedSortSavedPlacesAlphabetical", {
        SavedPlaceName: savedList?.saved_places[0].name,
        SavedPlaceId: savedList?.saved_places[0].id,
        SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
      });
    }

    setCurrentSort(value as any);
  };

  const handleListShare = () => {
    console.log("shared successfully!");
    AnalyticsService.trackGAEventHandler("viewListClickedShare");
    AnalyticsService.trackMx("viewListClickedShare", {
      SavedPlaceName: savedList?.saved_places[0].name,
      SavedPlaceId: savedList?.saved_places[0].id,
      SavedPlaceTotalPlaces: savedList?.saved_places[0].total_places,
    });
  };


  // Handle selectedVenue changes (for map marker clicks)
  useEffect(() => {
    // Skip if we're syncing from URL (to avoid duplicate pushState)
    if (isSyncingFromUrl.current) {
      isSyncingFromUrl.current = false;
      return;
    }

    if (selectedVenue) {
      onVenueClick(selectedVenue);
    } else {
      trackMobile.current?.prev();
      setCurrentVenueTypePage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue]);

  // Accessibility: Manage focus when switching between venue list and details
  useEffect(() => {
    if (selectedVenue && venueDetailsContainerRef.current) {
      venueDetailsContainerRef.current.focus();
    } else if (!selectedVenue && venueListContainerRef.current) {
      venueListContainerRef.current.focus();
    }
  }, [selectedVenue]);

  // Handle Escape key for detail view overlays
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (showAccessibilityDetail) {
          setShowAccessibilityDetail(false);
          setAccessibilityDetailSpaceId("");
        }
        if (showPhotosDetail) {
          setShowPhotosDetail(false);
          setPhotosDetailCategoryId(null);
        }
      }
    };
    if (showAccessibilityDetail || showPhotosDetail) {
      window.addEventListener("keydown", handleEscape);
      return () => window.removeEventListener("keydown", handleEscape);
    }
  }, [showAccessibilityDetail, showPhotosDetail]);

  // Sync URL venue parameter with selectedVenue state (for browser back/forward buttons)
  useEffect(() => {
    if (venue) {
      // Find venue by slug in locations
      const matchedVenue = locations.find((loc) => loc.business.slug === venue);
      if (matchedVenue && matchedVenue.id !== selectedVenue?.id) {
        // Set flag to prevent the selectedVenue useEffect from calling onVenueClick
        isSyncingFromUrl.current = true;
        setSelectedVenue(matchedVenue);
        setCurrentVenueTypePage(1);
        trackMobile.current?.next();
      }
    } else if (!venue && selectedVenue) {
      // URL has no venue but state has one - clear it
      isSyncingFromUrl.current = true;
      setSelectedVenue(null);
      setCurrentVenueTypePage(0);
      trackMobile.current?.prev();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue, locations]);

  const onVenueClick = (location: Venue) => {
    console.log("onVenueClick", location);
    AnalyticsService.trackGAEventHandler("viewListClickedViewVenue");
    AnalyticsService.trackMx("viewListClickedViewVenue", {
      VenueId: location.id,
      VenueBrandId: location.brandId,
      VenueBrandName: location.brandName,
      VenueAddress: location.address,
      VenueName: location.name,
      VenueType: location.businessType,
    });

    if (location.business.templateId === 5) {
      window.open(`/whats-nearby/${location.business.slug}`, "_blank");
      return;
    }

    if (
      location.business.templateId != undefined &&
      location.business.templateId != null &&
      location.business.templateId > 1 &&
      location.business.publicUrl &&
      location.business.publicUrl !== ""
    ) {
      window.open(`/venuedetails/${location.business.publicUrl}`, "_blank");
      return;
    }

    setCurrentVenueTypePage(1);
    trackMobile.current?.next();
    setTimeout(() => {
      setSelectedVenue(location);
    }, 400);

    // Update browser URL with venue slug as query parameter (avoids route execution)
    const url = new URL(window.location.href);
    url.searchParams.set("venue", location.business.slug);
    window.history.pushState({}, "", url.toString());
  };

  // Handle get alert
  const handleGetAlert = useCallback(
    (venueId: string | number) => {
      if (!isLoggedIn) {
        showModal(AuthModalScreen.EnterEmail);
        return;
      }

      // Find the venue by ID from locations
      const venue = locations.find((v) => v.id === venueId);

      if (venue) {
        const businessId = venue.business?.id;

        // CheckIcon if this venue already has an interest record
        const existingInterest: BusinessInterestType | undefined = businessId
          ? businessInterestMap.get(businessId)
          : undefined;

        setSelectedVenueForNotification({
          id: businessId?.toString() || venueId.toString(),
          name: venue.name,
          interestId: existingInterest?.id.toString(),
          interestedType: existingInterest?.interested_type,
          notifyAccessibilityInformation:
            existingInterest?.notify_accessibility_information === 1,
          notifyLiveAlert: existingInterest?.notify_live_alert === 1,
        });
        setIsNotificationModalOpen(true);
      }
    },
    [isLoggedIn, showModal, locations, businessInterestMap],
  );

  const handleNotificationModalClose = useCallback(() => {
    setIsNotificationModalOpen(false);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {savedPlaceId === "all"
            ? i18n.get("allSavedPlaces")
            : `${savedList?.saved_places[0]?.name} | Sociability`}
        </title>
      </Helmet>

      <main
        id="main"
        className="h-[calc(100vh-90px)] bg-bg-brand-secondary-default flex overflow-x-hidden max-w-[1440px] mx-auto"
        aria-hidden={isAuthModalVisible ? "true" : "false"}
      >
        {/* Screen reader heading for page context */}
        <h1 className="sr-only">
          {savedPlaceId === "all"
            ? i18n.get("allSavedPlaces")
            : savedList?.saved_places[0]?.name || i18n.get("savedPlaces")}
        </h1>

        {/* Desktop Accessibility Detail View â€” full screen overlay */}
        {showAccessibilityDetail && venueDetailsData && (
          <section className="hidden md:flex h-full bg-bg-brand-secondary-default flex-col flex-1">
            <div className="flex-1 min-h-0 overflow-hidden">
              <AccessibilityDetailView
                venueDetails={venueDetailsData.details}
                accessibilityCategories={venueDetailsData.accesibilityCategories || []}
                initialSpaceId={accessibilityDetailSpaceId}
                onClose={() => {
                  AnalyticsService.trackGAEventHandler("savedPlacesAccessibilityDetailClosed");
                  AnalyticsService.trackMx("savedPlacesAccessibilityDetailClosed", {
                    VenueId: selectedVenue?.id,
                    VenueName: selectedVenue?.name,
                  });
                  setShowAccessibilityDetail(false);
                  setAccessibilityDetailSpaceId("");
                }}
                onShare={(spaceId) => {
                  if (navigator.share) {
                    navigator.share({
                      title: venueDetailsData.details.name,
                      text: `CheckIcon out accessibility information at ${venueDetailsData.details.name}`,
                      url: `${window.location.origin}/accessibility-guide-space/${venueDetailsData.details.business.slug}/${spaceId}`,
                    });
                  }
                }}
              />
            </div>
          </section>
        )}

        {/* Desktop Photos Detail View â€” full screen overlay */}
        {venueDetailsData && showPhotosDetail && (
          <section className="hidden md:flex flex-col h-[calc(100vh-90px)] bg-bg-brand-secondary-default">
            <div className="flex-1 min-h-0 overflow-hidden">
              <PhotosDetailView
                venueDetailsResponse={venueDetailsData}
                accessibilityCategories={venueDetailsData.accesibilityCategories || []}
                initialCategoryId={photosDetailCategoryId}
                venueName={venueDetailsData.details.name}
                onClose={() => {
                  AnalyticsService.trackGAEventHandler("savedPlacesPhotosDetailClosed");
                  AnalyticsService.trackMx("savedPlacesPhotosDetailClosed", {
                    VenueId: selectedVenue?.id,
                    VenueName: selectedVenue?.name,
                  });
                  setShowPhotosDetail(false);
                  setPhotosDetailCategoryId(null);
                }}
              />
            </div>
          </section>
        )}

        {/* DESKTOP VIEW */}
        <div
          className={cn(
            "hidden md:flex flex-1 min-w-0",
            (showAccessibilityDetail || showPhotosDetail) && "!hidden",
          )}
        >
          <div
            className={cn(
              "flex flex-col min-w-0",
              isLeftPaneVisible ? "flex" : "hidden",
              selectedVenue ? "" : "py-[10px] pb-[20px] pr-[3px]",
              !isLeftPaneVisible
                ? "w-0"
                : selectedVenue
                  ? "w-[calc(100%-360px)]"
                  : "w-1/2",
            )}
          >
            {/* Venue List â€” shown when no venue is selected */}
            <div
              ref={venueListContainerRef}
              data-scroll-container
              className={cn(
                "flex-1 overflow-y-auto",
                selectedVenue ? "hidden" : "",
              )}
              aria-hidden={selectedVenue ? "true" : "false"}
              {...(selectedVenue ? { inert: true } : {})}
              tabIndex={-1}
            >
              <div
                ref={venueDetailsScrollRef}
                className="pl-[32px] pr-[16px] h-full"
                onScroll={(e) => {
                  setScrollToTopVisible(e.currentTarget.scrollTop > 100);
                }}
              >
                {/* ROW 1 â€” back, share, menu */}
                <div className="pt-[10px] pb-[4px] flex justify-between items-center">
                  {/* Left area */}
                  {isLoggedIn && isUserOwner && (
                    <div className="flex items-center">
                      <StandardButton
                        onClick={() => {
                          AnalyticsService.trackGAEventHandler(
                            "viewListClickedBackButton",
                          );
                          AnalyticsService.trackMx(
                            "viewListClickedBackButton",
                            {},
                          );
                          goBackToSavedPlaces();
                        }}
                        aria-label={`Back to ${i18n.get("mySavedPlaces")}`}
                        variant="backListView"
                        className="flex items-center gap-2 px-1 py-3 bg-bg-brand-secondary-default text-center body-l-semibold !text-fg-brand-subtle"
                        imageSrc={
                          isDark
                            ? "/images/ArrowLeftIcon_dark.svg"
                            : "/images/ArrowLeftIcon.svg"
                        }
                        imageAlt=""
                        imageContainerClassName="w-6 h-6 relative"
                      >
                        {i18n.get("mySavedPlaces")}
                      </StandardButton>
                    </div>
                  )}

                  {/* Right areas */}
                  <div className="flex items-center gap-4">
                    <RWebShare
                      data={{
                        text: i18n.get("seeMyFavSociabilityPlacesText"),
                        url:
                          window.location.origin +
                          "/savedplacedetails/" +
                          savedPlaceId +
                          "/user/" +
                          userId,
                        title: i18n.get("seeMyFavSociabilityPlacesTitle"),
                      }}
                      onClick={() => {
                        handleListShare();
                      }}
                    >
                      <StandardButton
                        type="button"
                        aria-label="Share List"
                        variant="ghost"
                        size="icon"
                        imageSrc={
                          isDark
                            ? "/images/share_list_dark.svg"
                            : "/images/share_list.svg"
                        }
                        imageAlt="Share List"
                        imageContainerClassName="w-6 h-6"
                      />
                    </RWebShare>

                    {isLoggedIn && isUserOwner && savedPlaceId !== "all" && (
                      <SavePlaceMenu
                        onDeleteClick={() => handleDeleteList()}
                        list={savedList?.saved_places[0]}
                      />
                    )}
                  </div>
                </div>

                {/* ROW 2 â€” title, description, sort */}
                <div className="py-[16px] flex flex-col items-start gap-[4px]">
                  
                  <div className="w-full grid grid-cols-1 md:grid-cols-[1fr_auto] gap-[4px] items-center">
                    <div className="title-s-semibold text-fg-neutral-strong">
                      {savedPlaceId === "all"
                        ? i18n.get("allSavedPlaces")
                        : savedList?.saved_places[0]?.name}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger>
                        <div className="flex flex-row items-center gap-2 cursor-pointer">
                          <span className="border-b border-bg-brand-primary-default label-s-medium">
                            {sortByText}
                          </span>
                          <CaretDownIcon size={20} aria-hidden="true" />
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        tabIndex={0}
                        className="focus-blue"
                      >
                        <DropdownMenuLabel
                          tabIndex={0}
                          aria-label={i18n.get("sortBy")}
                          className="focus-blue"
                        >
                          {i18n.get("sortBy")}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuRadioGroup
                          tabIndex={0}
                          value={currentSort}
                          onValueChange={(value) => {
                            handleListSort(value as any);
                          }}
                          className="focus-blue"
                        >
                          <DropdownMenuRadioItem
                            value="latest"
                            tabIndex={0}
                            className="focus-blue"
                          >
                            Date added (Newest)
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem
                            value="earliest"
                            tabIndex={0}
                            className="focus-blue"
                          >
                            Date added (Oldest)
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem
                            value="alphabetical"
                            tabIndex={0}
                            className="focus-blue"
                          >
                            Alphabetical
                          </DropdownMenuRadioItem>
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <VenueList
                  locations={locations}
                  isLoading={isLocationsLoading}
                  onVenueClick={onVenueClick}
                  onGetAlert={handleGetAlert}
                  notificationVenueIds={notificationVenueIds}
                  isLoggedIn={isLoggedIn}
                  onShowAuthModal={() =>
                    showModal(AuthModalScreen.EnterEmail)
                  }
                />
              </div>
            </div>

            {/* Venue Details â€” shown when a venue is selected */}
            <div
              ref={venueDetailsContainerRef}
              className={cn(
                "flex-1 overflow-y-auto",
                !selectedVenue ? "hidden" : "",
              )}
              aria-hidden={!selectedVenue ? "true" : "false"}
              {...(!selectedVenue ? { inert: true } : {})}
              tabIndex={-1}
            >
              {selectedVenue && (
                <VenueDetailBasics
                  selectedVenue={selectedVenue}
                  onBack={() => {
                    setSelectedVenue(null);
                    // Remove venue parameter from URL
                    const url = new URL(window.location.href);
                    url.searchParams.delete("venue");
                    window.history.pushState({}, "", url.toString());
                  }}
                  openAllPhotosInline={false}
                  currentVenueTypePage={currentVenueTypePage}
                  onShowDetailView={(initialSpaceId) => {
                    AnalyticsService.trackGAEventHandler("savedPlacesAccessibilityDetailOpened");
                    AnalyticsService.trackMx("savedPlacesAccessibilityDetailOpened", {
                      VenueId: selectedVenue?.id,
                      VenueName: selectedVenue?.name,
                      InitialSpaceId: initialSpaceId,
                    });
                    setAccessibilityDetailSpaceId(initialSpaceId);
                    setShowAccessibilityDetail(true);
                  }}
                  onShowPhotosDetailView={(initialCategoryId) => {
                    AnalyticsService.trackGAEventHandler("savedPlacesPhotosDetailOpened");
                    AnalyticsService.trackMx("savedPlacesPhotosDetailOpened", {
                      VenueId: selectedVenue?.id,
                      VenueName: selectedVenue?.name,
                      InitialCategoryId: initialCategoryId,
                    });
                    setPhotosDetailCategoryId(initialCategoryId);
                    setShowPhotosDetail(true);
                  }}
                />
              )}
            </div>

            {scrollToTopVisible && !selectedVenue && (
              <button
                type="button"
                onClick={() => {
                  venueDetailsScrollRef?.current?.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  });
                }}
                className="bg-fg-neutral-strong rounded-sm absolute left-[calc(50vw-80px)] bottom-6 p-2 cursor-pointer shadow-md back-to-top-arrow border-0 focus-blue"
                aria-label={i18n.get("scrollToTop")}
              >
                <ArrowUp color="#fff" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Map sidebar â€” always visible on desktop */}
          <div
            className={cn(
              "min-w-0 pl-[2px] py-[10px]",
              !isLeftPaneVisible
                ? "w-full"
                : selectedVenue
                  ? "w-[360px]"
                  : "w-1/2",
            )}
          >
            <BasicMapView
              query={query}
              currentVenueTypePage={currentVenueTypePage}
            />
          </div>
        </div>

        {/* Mobile Accessibility Detail View â€” full screen overlay */}
        {showAccessibilityDetail && venueDetailsData && (
          <section className="flex md:hidden h-full bg-bg-brand-secondary-default flex-col flex-1 w-full">
            <div className="flex-1 min-h-0 overflow-hidden">
              <AccessibilityDetailView
                venueDetails={venueDetailsData.details}
                accessibilityCategories={venueDetailsData.accesibilityCategories || []}
                initialSpaceId={accessibilityDetailSpaceId}
                onClose={() => {
                  AnalyticsService.trackGAEventHandler("savedPlacesAccessibilityDetailClosed");
                  AnalyticsService.trackMx("savedPlacesAccessibilityDetailClosed", {
                    VenueId: selectedVenue?.id,
                    VenueName: selectedVenue?.name,
                  });
                  setShowAccessibilityDetail(false);
                  setAccessibilityDetailSpaceId("");
                }}
                onShare={(spaceId) => {
                  if (navigator.share) {
                    navigator.share({
                      title: venueDetailsData.details.name,
                      text: `CheckIcon out accessibility information at ${venueDetailsData.details.name}`,
                      url: `${window.location.origin}/accessibility-guide-space/${venueDetailsData.details.business.slug}/${spaceId}`,
                    });
                  }
                }}
              />
            </div>
          </section>
        )}

        {/* Mobile Photos Detail View â€” full screen overlay */}
        {venueDetailsData && showPhotosDetail && (
          <section className="flex md:hidden flex-col h-[calc(100vh-90px)] bg-bg-brand-secondary-default">
            <div className="flex-1 min-h-0 overflow-hidden">
              <PhotosDetailView
                venueDetailsResponse={venueDetailsData}
                accessibilityCategories={venueDetailsData.accesibilityCategories || []}
                initialCategoryId={photosDetailCategoryId}
                venueName={venueDetailsData.details.name}
                onClose={() => {
                  AnalyticsService.trackGAEventHandler("savedPlacesPhotosDetailClosed");
                  AnalyticsService.trackMx("savedPlacesPhotosDetailClosed", {
                    VenueId: selectedVenue?.id,
                    VenueName: selectedVenue?.name,
                  });
                  setShowPhotosDetail(false);
                  setPhotosDetailCategoryId(null);
                }}
              />
            </div>
          </section>
        )}

        {/* MOBILE VIEW */}
        <div
          className={cn(
            "flex md:hidden flex-col flex-1 w-full",
            (showAccessibilityDetail || showPhotosDetail) && "hidden",
          )}
        >
          {/* Sticky header â€” back, share, menu (only on list page) */}
          {currentVenueTypePage === 0 && (
            <div className="sticky top-0 z-40 bg-bg-brand-secondary-default">
              <div className="px-4 py-3">
                <div className="flex justify-between items-center">
                  {/* Left area */}
                  {isLoggedIn && isUserOwner && (
                    <button
                      type="button"
                      onClick={() => {
                        AnalyticsService.trackGAEventHandler(
                          "viewListClickedBackButton",
                        );
                        AnalyticsService.trackMx(
                          "viewListClickedBackButton",
                          {},
                        );
                        onBack();
                      }}
                      className="flex items-center gap-2 py-2 rounded focus-blue"
                      aria-label={`Back to ${i18n.get("mySavedPlaces")}`}
                    >
                      <div className="w-6 h-6 relative">
                        <img
                          className="w-6 h-6"
                          src={
                            isDark
                              ? "/images/ArrowLeftIcon_dark.svg"
                              : "/images/ArrowLeftIcon.svg"
                          }
                          alt=""
                          aria-hidden="true"
                        />
                      </div>
                      <span className="ml-2 body-l-semibold !text-fg-brand-subtle">
                        {i18n.get("mySavedPlaces")}
                      </span>
                    </button>
                  )}

                  {/* Right areas */}
                  <div className="flex items-center gap-4">
                    <RWebShare
                      data={{
                        text: i18n.get("seeMyFavSociabilityPlacesText"),
                        url:
                          window.location.origin +
                          "/savedplacedetails/" +
                          savedPlaceId +
                          "/user/" +
                          userId,
                        title: i18n.get("seeMyFavSociabilityPlacesTitle"),
                      }}
                      onClick={() => {
                        handleListShare();
                      }}
                    >
                      <StandardButton
                        type="button"
                        aria-label="Share List"
                        variant="ghost"
                        size="icon"
                        imageSrc={
                          isDark
                            ? "/images/share_list_dark.svg"
                            : "/images/share_list.svg"
                        }
                        imageAlt="Share List"
                        imageContainerClassName="w-6 h-6"
                      />
                    </RWebShare>

                    {isLoggedIn && isUserOwner && savedPlaceId !== "all" && (
                      <SavePlaceMenu
                        onDeleteClick={() => handleDeleteList()}
                        list={savedList?.saved_places[0]}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Floating List/Map Toggle Button â€” outside scroll div */}
          {currentVenueTypePage === 0 && (
            <button
              type="button"
              onClick={() => {
                setMobileListView(!mobileListView);
              }}
              className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 p-3
                rounded-full border-0 cursor-pointer flex items-center gap-2
                shadow-lg bg-bg-brand-secondary-default
                active:bg-bg-brand-secondary-active focus-blue
                transition-all duration-200"
              aria-label={mobileListView ? i18n.get("showMapView") : i18n.get("showListView")}
            >
              {mobileListView ? (
                <>
                  <MapTrifoldIcon size={24} className="text-fg-neutral-strong" aria-hidden="true" />
                  <span className="label-m-medium text-fg-neutral-strong">
                    {i18n.get("map")}
                  </span>
                </>
              ) : (
                <>
                  <ListBulletsIcon size={24} className="text-fg-neutral-strong" aria-hidden="true" />
                  <span className="label-m-medium text-fg-neutral-strong">
                    {i18n.get("list")}
                  </span>
                </>
              )}
            </button>
          )}

          {/* Mobile Content Area */}
          <div
            ref={mobileVenueDetailsScrollRef}
            className={cn(
              "flex-1 pb-[20px]",
              currentVenueTypePage === 0 ? "overflow-y-auto" : "",
            )}
            onScroll={(e) => {
              const scrollPosition = e.currentTarget.scrollTop;
              setMobileScrollToTopVisible(scrollPosition > 100);
            }}
          >
            {/* List View */}
            <div className={cn(mobileListView ? "block" : "hidden")}>
              <ViewPager tag="div" tabIndex={-1} aria-label="Mobile content view pager">
                <Frame className="frame">
                  <Track
                    ref={trackMobile}
                    viewsToShow={1}
                    className="track"
                    swipe={false}
                    accessibility={false}
                  >
                    <View>
                      {currentVenueTypePage === 0 && (
                        <div className="px-4">
                          {/* Title and Sort section */}
                          <div className="pt-[8px] pb-[16px] flex flex-col items-start gap-[4px]">                            
                            <div className="text-fg-neutral-strong title-s-semibold">
                              {savedPlaceId === "all"
                                ? i18n.get("allSavedPlaces")
                                : savedList?.saved_places[0]?.name}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger>
                                <div className="flex flex-row items-center gap-2 cursor-pointer">
                                  <span className="border-b border-bg-brand-primary-default label-s-medium">
                                    {sortByText}
                                  </span>
                                  <CaretDownIcon size={20} aria-hidden="true" />
                                </div>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" tabIndex={0} className="focus-blue">
                                <DropdownMenuLabel
                                  tabIndex={0}
                                  aria-label={i18n.get("sortBy")}
                                  className="focus-blue"
                                >
                                  {i18n.get("sortBy")}
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuRadioGroup
                                  tabIndex={0}
                                  value={currentSort}
                                  onValueChange={(value) => {
                                    handleListSort(value as any);
                                  }}
                                  className="focus-blue"
                                >
                                  <DropdownMenuRadioItem
                                    value="latest"
                                    tabIndex={0}
                                    className="focus-blue"
                                  >
                                    Date added (Newest)
                                  </DropdownMenuRadioItem>
                                  <DropdownMenuRadioItem
                                    value="earliest"
                                    tabIndex={0}
                                    className="focus-blue"
                                  >
                                    Date added (Oldest)
                                  </DropdownMenuRadioItem>
                                  <DropdownMenuRadioItem
                                    value="alphabetical"
                                    tabIndex={0}
                                    className="focus-blue"
                                  >
                                    Alphabetical
                                  </DropdownMenuRadioItem>
                                </DropdownMenuRadioGroup>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                          <VenueList
                            locations={locations}
                            isLoading={isLocationsLoading}
                            onVenueClick={onVenueClick}
                            onGetAlert={handleGetAlert}
                            notificationVenueIds={notificationVenueIds}
                            isLoggedIn={isLoggedIn}
                            onShowAuthModal={() =>
                              showModal(AuthModalScreen.EnterEmail)
                            }
                          />
                        </div>
                      )}
                    </View>
                    <View className="">
                      {selectedVenue && (
                        <VenueDetailBasics
                          selectedVenue={selectedVenue}
                          onBack={() => {
                            trackMobile.current?.prev();
                            setCurrentVenueTypePage(0);
                            setTimeout(() => {
                              setSelectedVenue(null);
                            }, 500);
                            // Remove venue parameter from URL
                            const url = new URL(window.location.href);
                            url.searchParams.delete("venue");
                            window.history.pushState({}, "", url.toString());
                          }}
                          openAllPhotosInline={false}
                          currentVenueTypePage={currentVenueTypePage}
                          onShowDetailView={(initialSpaceId) => {
                            setAccessibilityDetailSpaceId(initialSpaceId);
                            setShowAccessibilityDetail(true);
                          }}
                          onShowPhotosDetailView={(initialCategoryId) => {
                            setPhotosDetailCategoryId(initialCategoryId);
                            setShowPhotosDetail(true);
                          }}
                        />
                      )}
                    </View>
                  </Track>
                </Frame>
              </ViewPager>
              {mobileScrollToTopVisible && (
                <button
                  type="button"
                  onClick={() => {
                    mobileVenueDetailsScrollRef?.current?.scrollTo({
                      top: 0,
                      behavior: "smooth",
                    });
                  }}
                  className="bg-fg-neutral-strong rounded-sm absolute right-6 bottom-6 p-2 cursor-pointer shadow-md border-0 focus-blue"
                  aria-label={i18n.get("scrollToTop")}
                >
                  <ArrowUp color="#fff" aria-hidden="true" />
                </button>
              )}
            </div>

            {/* Map View */}
            <div
              className={cn(mobileListView ? "hidden" : "flex flex-col h-full")}
            >
              <div className="flex-1 min-h-[600px]">
                <BasicMapView
                  query={query}
                  hidden={mobileListView}
                  currentVenueTypePage={currentVenueTypePage}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Notification Modal */}
      {selectedVenueForNotification && (
        <InterestNotificationModal
          businessId={selectedVenueForNotification.id}
          businessName={selectedVenueForNotification.name}
          interestId={selectedVenueForNotification.interestId}
          interestedType={selectedVenueForNotification.interestedType}
          notifyAccessibilityInformation={
            selectedVenueForNotification.notifyAccessibilityInformation
          }
          notifyLiveAlert={selectedVenueForNotification.notifyLiveAlert}
          isOpen={isNotificationModalOpen}
          onClose={handleNotificationModalClose}
          onSuccess={() => {
            console.log("Notification preferences saved successfully");
          }}
        />
      )}
    </>
  );
}
